import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  parseISO,
} from 'date-fns';
import { enUS } from 'date-fns/locale';

import { toShortDateFormat } from '@shared/utils/dateFormatters';
import type { Message } from './apiHelpers';

/**
 * Returns one row per guest: the message with the latest `createdAt` in that
 * thread (any sender), considering only messages in `messages`. Rows are ordered
 * by that latest timestamp, newest first.
 */
export function buildConversationSummaries(messages: Message[]): Message[] {
  const sorted = [...messages].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const seenUserIds = new Set<string>();
  const summaries: Message[] = [];

  for (const message of sorted) {
    if (!seenUserIds.has(message.user.id)) {
      seenUserIds.add(message.user.id);
      summaries.push(message);
    }
  }

  return summaries;
}

export interface InboxConversationsByDay {
  dayKey: string;
  sectionLabel: string;
  conversations: Message[];
}

/**
 * Buckets the conversation list by local calendar day of the latest message
 * in each thread. Preserves the input order: newest days first, then within
 * a day, same order as `conversations` (newest last activity first).
 */
export function groupInboxConversationsByDay(
  conversations: Message[],
): InboxConversationsByDay[] {
  const groups: InboxConversationsByDay[] = [];
  const dayKeyToIndex = new Map<string, number>();

  for (const message of conversations) {
    const date = parseISO(message.createdAt);
    const dayKey = format(date, 'yyyy-MM-dd');
    if (!dayKeyToIndex.has(dayKey)) {
      dayKeyToIndex.set(dayKey, groups.length);
      groups.push({
        dayKey,
        sectionLabel: formatInboxDateSectionLabel(message.createdAt),
        conversations: [],
      });
    }
    const idx = dayKeyToIndex.get(dayKey)!;
    groups[idx].conversations.push(message);
  }

  return groups;
}

export function formatInboxDateSectionLabel(iso: string): string {
  const date = parseISO(iso);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return toShortDateFormat(date, false);
}

export function formatInboxConversationRelativeTime(iso: string): string {
  return formatDistanceToNow(parseISO(iso), {
    addSuffix: true,
    locale: enUS,
  }).toUpperCase();
}
